// src/components/PowerBIReportViewer.tsx
// Embedded Power BI paginated report viewer with capacity management.
// Uses powerbi-client SDK for embedding — NOT a raw iframe.
// Dependency required: npm install powerbi-client

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pbi from 'powerbi-client';
import { models } from 'powerbi-client';
import {
  Play,
  Pause,
  RefreshCw,
  Clock,
  AlertCircle,
  Zap,
  ZapOff,
  FileText,
  Maximize2,
  Minimize2,
  ArrowLeft,
  Settings,
  Database,
  AlertTriangle,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface PowerBIReportViewerProps {
  onBack?: () => void;
  onSettings?: () => void;
}

interface ReportConfig {
  id: string;
  name: string;
  description?: string;
}

interface EmbedConfig {
  reportId: string;
  reportName: string;
  reportType: string;
  embedUrl: string;
  embedToken: string;
  expiration: string;
}

type CapacityState = 'Active' | 'Succeeded' | 'Paused' | 'Starting' | 'Stopping' | 'Unknown';

interface CapacityStatus {
  state: CapacityState;
  lastChecked: Date;
}

interface DatasetRefreshStatus {
  status: 'Completed' | 'InProgress' | 'Failed' | 'Unknown' | 'Unavailable';
  lastRefresh: Date | null;
  nextRefresh: Date | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════

const AUTO_PAUSE_MINUTES = 15;
const AUTO_PAUSE_WARNING_MINUTES = 2;

// ═══════════════════════════════════════════════════════════════════════════
// API Helper
// ═══════════════════════════════════════════════════════════════════════════

const PROXY_URL = '/.netlify/functions/sharepoint-proxy';

interface ProxyResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function apiRequest<T>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const result: ProxyResponse<T> = await response.json();
  if (!result.success) throw new Error(result.error || 'Request failed');
  return result.data as T;
}

// ═══════════════════════════════════════════════════════════════════════════
// Power BI service singleton
// ═══════════════════════════════════════════════════════════════════════════

const powerbi = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

// ═══════════════════════════════════════════════════════════════════════════
// Available Reports
// ═══════════════════════════════════════════════════════════════════════════

const AVAILABLE_REPORTS: ReportConfig[] = [
  {
    id: '4d5160de-cb87-4b4d-90d4-af9ae71e39ff',
    name: 'RouteSheet - All Drivers',
    description: 'Load sheet for all drivers',
  },
  {
    id: '38450c8a-a814-4211-bd98-e9790ea016af',
    name: 'RouteSheet',
    description: 'Load sheet by driver',
  },
  {
    id: '89eec3c2-3a1a-4559-ac8f-e80333bdc036',
    name: 'RouteItems - All',
    description: 'All route items',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// Helper — is capacity in a usable state?
// ═══════════════════════════════════════════════════════════════════════════

const isCapacityReady = (state: CapacityState) =>
  state === 'Active' || state === 'Succeeded';

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

const PowerBIReportViewer: React.FC<PowerBIReportViewerProps> = ({ onBack, onSettings }) => {
  const embedContainerRef = useRef<HTMLDivElement>(null);

  const [selectedReport, setSelectedReport] = useState<ReportConfig | null>(null);
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null);
  const [capacityStatus, setCapacityStatus] = useState<CapacityStatus>({
    state: 'Unknown',
    lastChecked: new Date(),
  });
  const [refreshStatus, setRefreshStatus] = useState<DatasetRefreshStatus>({
    status: 'Unknown',
    lastRefresh: null,
    nextRefresh: null,
  });
  const [refreshUnavailable, setRefreshUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  // Ref-based state for use inside intervals (avoids stale closure issues)
  const capacityStateRef = useRef<CapacityState>('Unknown');
  const lastActivityRef = useRef<number>(Date.now());
  const isRefreshingRef = useRef(false);
  const isPausingRef = useRef(false); // prevent double-pause calls

  const [idleWarning, setIdleWarning] = useState(false);
  const [idleMinutesLeft, setIdleMinutesLeft] = useState(AUTO_PAUSE_MINUTES);

  // Report parameters
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedDriver, setSelectedDriver] = useState<string>('ALL');

  // Keep ref in sync with state
  const updateCapacityStatus = useCallback((state: CapacityState) => {
    capacityStateRef.current = state;
    setCapacityStatus({ state, lastChecked: new Date() });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // Activity tracking
  // ═══════════════════════════════════════════════════════════════════════════

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleWarning(false);
    setIdleMinutesLeft(AUTO_PAUSE_MINUTES);
    isPausingRef.current = false;
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // Auto-pause idle timer
  // Uses capacityStateRef (not state) to avoid stale closure in setInterval.
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const interval = setInterval(async () => {
      const currentState = capacityStateRef.current;
      if (!isCapacityReady(currentState)) return;
      if (isPausingRef.current) return;

      const idleMs = Date.now() - lastActivityRef.current;
      const idleMinutes = idleMs / 60000;
      const minutesLeft = Math.ceil(AUTO_PAUSE_MINUTES - idleMinutes);

      if (idleMinutes >= AUTO_PAUSE_MINUTES) {
        isPausingRef.current = true;
        try {
          await apiRequest('suspendCapacity');
          updateCapacityStatus('Paused');
          setIdleWarning(false);
          setEmbedConfig(null);
          if (embedContainerRef.current) {
            powerbi.reset(embedContainerRef.current);
          }
        } catch (err) {
          console.error('Auto-pause failed:', err);
          isPausingRef.current = false;
        }
        return;
      }

      if (idleMinutes >= AUTO_PAUSE_MINUTES - AUTO_PAUSE_WARNING_MINUTES) {
        setIdleWarning(true);
        setIdleMinutesLeft(minutesLeft);
      } else {
        setIdleWarning(false);
        setIdleMinutesLeft(minutesLeft);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [updateCapacityStatus]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Embed report using powerbi-client SDK
  // ═══════════════════════════════════════════════════════════════════════════

  const embedReport = useCallback(
    (config: EmbedConfig) => {
      if (!embedContainerRef.current) return;

      const url = new URL(config.embedUrl);
      url.searchParams.set('rp:ReqDate', reportDate);
      if (selectedDriver && selectedDriver !== 'ALL') {
        url.searchParams.set('rp:Driver', selectedDriver);
      }

      const embedConfiguration: models.IReportEmbedConfiguration = {
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken: config.embedToken,
        embedUrl: url.toString(),
        id: config.reportId,
        settings: {
          navContentPaneEnabled: false,
          filterPaneEnabled: false,
        },
      };

      powerbi.embed(embedContainerRef.current, embedConfiguration);
    },
    [reportDate, selectedDriver]
  );

  useEffect(() => {
    if (embedConfig) embedReport(embedConfig);
    return () => {
      if (embedContainerRef.current) powerbi.reset(embedContainerRef.current);
    };
  }, [embedConfig, embedReport]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Capacity Management
  // ═══════════════════════════════════════════════════════════════════════════

  const checkCapacityStatus = useCallback(async () => {
    try {
      const result = await apiRequest<{ state: string }>('getCapacityStatus');
      updateCapacityStatus(result.state as CapacityState);
    } catch (err) {
      console.error('Failed to check capacity:', err);
    }
  }, [updateCapacityStatus]);

  const resumeCapacity = useCallback(async () => {
    setCapacityLoading(true);
    try {
      await apiRequest('resumeCapacity');
      updateCapacityStatus('Starting');
      recordActivity();

      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await apiRequest<{ state: string }>('getCapacityStatus');
          const state = r.state as CapacityState;
          if (isCapacityReady(state) || attempts >= 30) {
            clearInterval(poll);
            updateCapacityStatus(state);
            setCapacityLoading(false);
          }
        } catch {
          if (attempts >= 30) {
            clearInterval(poll);
            setCapacityLoading(false);
          }
        }
      }, 10000);
    } catch (err) {
      console.error('Failed to resume capacity:', err);
      setCapacityLoading(false);
    }
  }, [recordActivity, updateCapacityStatus]);

  const stopCapacity = async () => {
    if (!confirm('Are you sure you want to pause the Power BI capacity? Reports will not be accessible until restarted.')) return;
    setCapacityLoading(true);
    setError(null);
    try {
      await apiRequest('suspendCapacity');
      updateCapacityStatus('Paused');
      setIdleWarning(false);
      setEmbedConfig(null);
      if (embedContainerRef.current) powerbi.reset(embedContainerRef.current);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop capacity');
    } finally {
      setCapacityLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Dataset Refresh
  // ═══════════════════════════════════════════════════════════════════════════

  const checkRefreshStatus = useCallback(async () => {
    if (refreshUnavailable) return;
    if (isRefreshingRef.current) return;
    try {
      const result = await apiRequest<{
        status: string;
        lastRefresh: string | null;
        nextRefresh: string | null;
      }>('getDatasetRefreshStatus');
      setRefreshStatus({
        status: result.status as DatasetRefreshStatus['status'],
        lastRefresh: result.lastRefresh ? new Date(result.lastRefresh) : null,
        nextRefresh: result.nextRefresh ? new Date(result.nextRefresh) : null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('403') || msg.includes('insufficient privileges')) {
        setRefreshUnavailable(true);
        setRefreshStatus({ status: 'Unavailable', lastRefresh: null, nextRefresh: null });
      } else {
        console.error('Failed to check refresh status:', err);
      }
    }
  }, [refreshUnavailable]);

  const triggerRefresh = async () => {
    setRefreshLoading(true);
    setError(null);
    isRefreshingRef.current = true;
    try {
      await apiRequest('refreshDataset');
      setRefreshStatus((prev) => ({ ...prev, status: 'InProgress' }));
      recordActivity();

      const pollInterval = setInterval(async () => {
        try {
          const result = await apiRequest<{ status: string; lastRefresh: string | null }>('getDatasetRefreshStatus');
          if (result.status !== 'InProgress') {
            clearInterval(pollInterval);
            isRefreshingRef.current = false;
            setRefreshStatus((prev) => ({
              ...prev,
              status: result.status as DatasetRefreshStatus['status'],
              lastRefresh: result.lastRefresh ? new Date(result.lastRefresh) : prev.lastRefresh,
            }));
            setRefreshLoading(false);
          }
        } catch {
          clearInterval(pollInterval);
          isRefreshingRef.current = false;
          setRefreshLoading(false);
        }
      }, 5000);
    } catch (err) {
      isRefreshingRef.current = false;
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('403') || msg.includes('insufficient privileges')) {
        setRefreshUnavailable(true);
        setRefreshStatus({ status: 'Unavailable', lastRefresh: null, nextRefresh: null });
      } else {
        setError(msg || 'Failed to refresh dataset');
      }
      setRefreshLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Report Loading
  // ═══════════════════════════════════════════════════════════════════════════

  const loadReport = async (report: ReportConfig) => {
    setLoading(true);
    setError(null);
    setSelectedReport(report);
    recordActivity();
    if (embedContainerRef.current) powerbi.reset(embedContainerRef.current);
    try {
      const config = await apiRequest<EmbedConfig>('getEmbedToken', { reportId: report.id });
      setEmbedConfig(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const refreshReport = () => {
    if (selectedReport) { recordActivity(); loadReport(selectedReport); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // On mount: silently resume capacity if paused, start polling
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const initCapacity = async () => {
      try {
        const result = await apiRequest<{ state: string }>('getCapacityStatus');
        const state = result.state as CapacityState;
        updateCapacityStatus(state);
        if (state === 'Paused') await resumeCapacity();
      } catch (err) {
        console.error('Failed to initialize capacity:', err);
      }
    };

    initCapacity();
    checkRefreshStatus();

    const interval = setInterval(() => {
      checkCapacityStatus();
      checkRefreshStatus();
    }, 60000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // Formatting helpers
  // ═══════════════════════════════════════════════════════════════════════════

  const formatDateTime = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const getCapacityColor = () => {
    switch (capacityStatus.state) {
      case 'Active':
      case 'Succeeded': return 'text-emerald-400 bg-emerald-500/20';
      case 'Paused':    return 'text-slate-400 bg-slate-500/20';
      case 'Starting':
      case 'Stopping':  return 'text-amber-400 bg-amber-500/20';
      default:          return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getRefreshStatusColor = () => {
    switch (refreshStatus.status) {
      case 'Completed':   return 'text-emerald-400';
      case 'InProgress':  return 'text-blue-400';
      case 'Failed':      return 'text-red-400';
      case 'Unavailable': return 'text-slate-500';
      default:            return 'text-slate-400';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Render: Idle Warning Banner
  // ═══════════════════════════════════════════════════════════════════════════

  const renderIdleWarning = () => {
    if (!idleWarning || !isCapacityReady(capacityStatus.state)) return null;
    return (
      <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center gap-3">
        <AlertTriangle size={18} />
        <span className="text-sm">
          Capacity will auto-pause in{' '}
          <strong>{idleMinutesLeft} minute{idleMinutesLeft !== 1 ? 's' : ''}</strong> due to inactivity.
        </span>
        <button
          onClick={recordActivity}
          className="ml-auto px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg text-sm transition-colors"
        >
          Keep active
        </button>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Render: Capacity Bar
  // ═══════════════════════════════════════════════════════════════════════════

  const renderCapacityBar = () => (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getCapacityColor()}`}>
            {isCapacityReady(capacityStatus.state) ? <Zap size={16} /> : <ZapOff size={16} />}
            <span className="text-sm font-medium">Capacity: {capacityStatus.state}</span>
          </div>

          {isCapacityReady(capacityStatus.state) && !idleWarning && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={12} />
              Auto-pause in {idleMinutesLeft}m
            </span>
          )}

          {(capacityStatus.state === 'Paused' || capacityStatus.state === 'Unknown') && (
            <button
              onClick={resumeCapacity}
              disabled={capacityLoading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {capacityLoading ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
              Start Capacity
            </button>
          )}

          {isCapacityReady(capacityStatus.state) && (
            <button
              onClick={stopCapacity}
              disabled={capacityLoading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <Pause size={16} />
              Pause Now
            </button>
          )}

          {capacityStatus.state === 'Starting' && (
            <span className="text-sm text-amber-400 flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin" />
              Starting capacity... (may take a few minutes)
            </span>
          )}
        </div>

        {/* Dataset Refresh */}
        <div className="flex items-center gap-4">
          {refreshUnavailable ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Database size={14} />
              <span>Dataset refresh requires Power BI admin API access</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Database size={16} className={getRefreshStatusColor()} />
                <div className="text-sm">
                  <span className="text-slate-400">Last refresh: </span>
                  <span className="text-white">
                    {refreshLoading ? 'Refreshing...' : formatDateTime(refreshStatus.lastRefresh)}
                  </span>
                </div>
              </div>
              <button
                onClick={triggerRefresh}
                disabled={refreshLoading || !isCapacityReady(capacityStatus.state)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                title={!isCapacityReady(capacityStatus.state) ? 'Start capacity first' : 'Refresh dataset'}
              >
                <RefreshCw size={14} className={refreshLoading ? 'animate-spin' : ''} />
                {refreshLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Render: Report Selection
  // ═══════════════════════════════════════════════════════════════════════════

  const renderReportSelection = () => (
    <div className="grid md:grid-cols-3 gap-4 mb-6">
      {AVAILABLE_REPORTS.map((report) => (
        <button
          key={report.id}
          onClick={() => loadReport(report)}
          disabled={loading}
          className={`p-5 rounded-xl border text-left transition-all ${
            selectedReport?.id === report.id
              ? 'bg-blue-500/20 border-blue-500/50'
              : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-700/50'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-lg ${
              selectedReport?.id === report.id ? 'bg-blue-500/30 text-blue-400' : 'bg-slate-700 text-slate-400'
            }`}>
              <FileText size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-white">{report.name}</h3>
              {report.description && <p className="text-sm text-slate-400 mt-1">{report.description}</p>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Render: Parameters (shown when a report is selected)
  // Settings gear is in the page header — not here.
  // ═══════════════════════════════════════════════════════════════════════════

  const renderParameters = () => (
    <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
      <div className="flex items-center gap-2">
        <label className="text-sm text-slate-400">Date:</label>
        <input
          type="date"
          value={reportDate}
          onChange={(e) => setReportDate(e.target.value)}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-slate-400">Driver:</label>
        <select
          value={selectedDriver}
          onChange={(e) => setSelectedDriver(e.target.value)}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="ALL">All Drivers</option>
          <option value="001">001</option>
          <option value="002">002</option>
          <option value="003">003</option>
        </select>
      </div>

      <button
        onClick={refreshReport}
        disabled={!selectedReport || loading}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
      >
        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        Apply
      </button>

      {/* Fullscreen toggle lives here too for convenience */}
      <div className="ml-auto">
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="p-2 rounded-lg bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Render: Embed Container
  // ═══════════════════════════════════════════════════════════════════════════

  const renderReportEmbed = () => {
    if (!embedConfig && !loading) {
      return (
        <div className="flex flex-col items-center justify-center h-96 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <FileText size={48} className="text-slate-600 mb-4" />
          <p className="text-slate-400">Select a report to view</p>
          {capacityStatus.state === 'Starting' && (
            <p className="text-amber-400 text-sm mt-2 flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin" />
              Capacity is starting up, reports will be available shortly...
            </p>
          )}
          {capacityStatus.state === 'Paused' && (
            <p className="text-amber-400 text-sm mt-2">Waiting for capacity to start...</p>
          )}
        </div>
      );
    }

    return (
      <div className={`bg-slate-800/30 rounded-xl border border-slate-700/50 overflow-hidden relative ${
        isFullscreen ? 'fixed inset-4 z-50' : ''
      }`}>
        {isFullscreen && (
          <div className="flex items-center justify-between p-3 bg-slate-800 border-b border-slate-700">
            <h3 className="font-semibold text-white">{embedConfig?.reportName}</h3>
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-2 rounded-lg bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
            >
              <Minimize2 size={18} />
            </button>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70 z-10">
            <RefreshCw size={32} className="animate-spin text-blue-400" />
          </div>
        )}

        {/* SDK embed target — must remain empty */}
        <div
          ref={embedContainerRef}
          className={`w-full ${isFullscreen ? 'h-[calc(100%-56px)]' : 'h-[700px]'}`}
        />
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Main Render
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-8">

        {/* ── Page Header ── */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <FileText className="text-blue-400" size={28} />
                Load / Route Sheets
              </h1>
              <p className="text-slate-400 mt-1 ml-10">View and print driver route sheets and order reports</p>
            </div>
          </div>

          {/* Settings gear — navigates to ReportScheduleManager */}
          <button
            onClick={() => onSettings?.()}
            className="p-2 rounded-lg border border-slate-700/50 bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            title="Report schedules & settings"
          >
            <Settings size={18} />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-500/20 rounded">×</button>
          </div>
        )}

        {/* Idle Warning */}
        {renderIdleWarning()}

        {renderCapacityBar()}
        {renderReportSelection()}
        {selectedReport && renderParameters()}
        {renderReportEmbed()}

        {/* Token expiry footer */}
        {embedConfig && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>Token expires: {formatDateTime(new Date(embedConfig.expiration))}</span>
            </div>
            <button onClick={refreshReport} className="text-blue-400 hover:text-blue-300 transition-colors">
              Refresh token
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerBIReportViewer;